import { useCallback, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

const DEBOUNCE_MS = 150;

export function useWidthObserver() {
  const [width, setWidth] = useState(0);
  const frameIdRef = useRef<number | null>(null);

  const updateWidth = useCallback((newWidth: number) => {
    // Cancel any pending frame before scheduling a new one
    if (frameIdRef.current) {
      cancelAnimationFrame(frameIdRef.current);
    }

    // Defer state update to avoid ResizeObserver loop warnings
    frameIdRef.current = requestAnimationFrame(() => {
      setWidth(newWidth);
      frameIdRef.current = null;
    });
  }, []);

  const debouncedUpdateWidth = useMemo(() => debounce(updateWidth, DEBOUNCE_MS), [updateWidth]);

  const resizeObserver = useMemo(
    () =>
      // Ignoring compat ResizeObserver seems sufficiently supported.
      // eslint-disable-next-line compat/compat
      new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;

        if (Number.isFinite(width)) {
          debouncedUpdateWidth(width);
        }
      }),
    [debouncedUpdateWidth],
  );

  const elementRef = useCallback<(instance: HTMLElement | null) => void>(
    instance => {
      if (instance) {
        resizeObserver.observe(instance);
      } else {
        // Cancel any pending debounced calls
        debouncedUpdateWidth.cancel();

        // Cancel any pending animation frame
        if (frameIdRef.current) {
          cancelAnimationFrame(frameIdRef.current);
          frameIdRef.current = null;
        }

        resizeObserver.disconnect();
      }
    },
    [resizeObserver, debouncedUpdateWidth],
  );
  return [width, elementRef] as const;
}
